import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function BusinessDevelopmentPage() {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Context Filter
    const adminFilter = location.state?.filterByAdmin || null;
    const adminNameDisplay = location.state?.adminName || null;

    const [loading, setLoading] = useState(true);

    const [stats, setStats] = useState({
        overview: {
            suTotal: 0, suActive: 0,
            empTotal: 0, empActive: 0,
            adminTotal: 0
        },
        sales: {
            totalLeads: 0, converted: 0, open: 0, conversionRate: 0, recentLeads: 0
        },
        demographics: {
            avgAge: 0, avgTenure: 0, retentionRate: 0, starters30Days: 0
        },
        growth: {
            serviceUsers: {}, employees: {}, admins: {} 
        },
        // Added Churn stats with same detailed structure
        churn: {
            serviceUsers: {}, employees: {}, admins: {}
        }
    });

    // Helper: Robust Date Parser
    const parseDate = (value) => {
        if (!value) return null;
        if (value.toDate) return value.toDate(); // Firestore Timestamp
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) return new Date(value); // String Date YYYY-MM-DD
        const d = new Date(value);
        return isNaN(d) ? null : d;
    };

    useEffect(() => {
        async function calculateBusinessStats() {
            try {
                // 1. Fetch Collections
                const [suSnap, empSnap, adminSnap, leadsSnap] = await Promise.all([
                    getDocs(collection(db, 'servers', serverId, 'serviceUsers')),
                    getDocs(collection(db, 'servers', serverId, 'employees')),
                    getDocs(collection(db, 'servers', serverId, 'admins')),
                    getDocs(collection(db, 'servers', serverId, 'leads'))
                ]);

                // 2. Filter Logic
                let suDocs = suSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                let empDocs = empSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                let adminDocs = adminSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                let leadDocs = leadsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                if (adminFilter) {
                    suDocs = suDocs.filter(d => d.adminID === adminFilter);
                    empDocs = empDocs.filter(d => d.adminID === adminFilter);
                    // Filter Leads by 'admin' field
                    leadDocs = leadDocs.filter(d => d.admin === adminFilter);
                }

                const now = new Date();

                // --- HELPER FUNCTIONS ---

                // 1. Growth Analysis
                const analyzeGrowth = (dataList) => {
                    const growth = { hour: 0, day: 0, week: 0, month: 0, quarter: 0, year: 0, total: 0 };
                    let activeCount = 0;

                    dataList.forEach(data => {
                        const isActive = data.active !== undefined ? data.active === true : data.status === true;
                        if (isActive) activeCount++;

                        const createdDate = parseDate(data.startDate) || parseDate(data.createdAt);

                        if (createdDate && !isNaN(createdDate)) {
                            const diffHours = (now - createdDate) / (1000 * 60 * 60);
                            const diffDays = diffHours / 24;

                            growth.total++;
                            if (diffHours <= 1) growth.hour++;
                            if (diffDays <= 1) growth.day++;
                            if (diffDays <= 7) growth.week++;
                            if (diffDays <= 30) growth.month++;
                            if (diffDays <= 90) growth.quarter++;
                            if (diffDays <= 365) growth.year++;
                        }
                    });

                    return { growth, activeCount, total: dataList.length };
                };

                // 2. Churn Analysis
                const analyzeChurn = (dataList) => {
                    const churn = { hour: 0, day: 0, week: 0, month: 0, quarter: 0, year: 0, total: 0 };

                    dataList.forEach(data => {
                        const isActive = data.active !== undefined ? data.active === true : data.status === true;
                        
                        // Only count INACTIVE users
                        if (!isActive) {
                            // Priority: Terminated Date > Left Date > Last Seen
                            const termDate = parseDate(data.terminatedDate) || parseDate(data.leftDate) || parseDate(data.lastSeen);

                            if (termDate && !isNaN(termDate)) {
                                const diffHours = (now - termDate) / (1000 * 60 * 60);
                                const diffDays = diffHours / 24;

                                churn.total++;
                                if (diffHours <= 1) churn.hour++;
                                if (diffDays <= 1) churn.day++;
                                if (diffDays <= 7) churn.week++;
                                if (diffDays <= 30) churn.month++;
                                if (diffDays <= 90) churn.quarter++;
                                if (diffDays <= 365) churn.year++;
                            }
                        }
                    });

                    return churn;
                };

                // 3. Demographics Analysis
                const analyzeDemographics = (employees) => {
                    let totalAge = 0, ageCount = 0;
                    let totalTenureDays = 0, tenureCount = 0;
                    let starters30Days = 0;

                    employees.forEach(emp => {
                        // Age
                        const dob = parseDate(emp.dob);
                        if (dob) {
                            const age = Math.abs(new Date(now - dob.getTime()).getUTCFullYear() - 1970);
                            totalAge += age;
                            ageCount++;
                        }

                        // Tenure & Starters
                        const startDate = parseDate(emp.startDate) || parseDate(emp.createdAt);
                        if (startDate) {
                            const diffDays = (now - startDate) / (1000 * 60 * 60 * 24);
                            totalTenureDays += diffDays;
                            tenureCount++;
                            if (diffDays <= 30) starters30Days++;
                        }
                    });

                    const avgAge = ageCount > 0 ? (totalAge / ageCount).toFixed(0) : 0;
                    const avgTenure = tenureCount > 0 ? (totalTenureDays / tenureCount / 365).toFixed(1) : 0;
                    
                    return { avgAge, avgTenure, starters30Days };
                };

                // 4. Sales Analysis
                const analyzeSales = (leads) => {
                    let converted = 0, recent = 0;
                    leads.forEach(l => {
                        if (l.converted === true) converted++;
                        const created = parseDate(l.createdAt);
                        if (created && (now - created) / (1000 * 60 * 60 * 24) <= 30) recent++;
                    });

                    return {
                        totalLeads: leads.length,
                        converted,
                        open: leads.length - converted,
                        conversionRate: leads.length > 0 ? ((converted / leads.length) * 100).toFixed(0) : 0,
                        recentLeads: recent
                    };
                };

                // --- EXECUTE ---
                const suAnalysis = analyzeGrowth(suDocs);
                const empAnalysis = analyzeGrowth(empDocs);
                const adminAnalysis = analyzeGrowth(adminDocs);

                // Calculate Churn
                const suChurn = analyzeChurn(suDocs);
                const empChurn = analyzeChurn(empDocs);
                const adminChurn = analyzeChurn(adminDocs);

                const demoAnalysis = analyzeDemographics(empDocs);
                const salesAnalysis = analyzeSales(leadDocs);

                const retention = empAnalysis.total > 0 
                    ? ((empAnalysis.activeCount / empAnalysis.total) * 100).toFixed(0) 
                    : 0;

                setStats({
                    overview: {
                        suTotal: suAnalysis.total, suActive: suAnalysis.activeCount,
                        empTotal: empAnalysis.total, empActive: empAnalysis.activeCount,
                        adminTotal: adminAnalysis.total
                    },
                    sales: salesAnalysis,
                    growth: {
                        serviceUsers: suAnalysis.growth,
                        employees: empAnalysis.growth,
                        admins: adminAnalysis.growth
                    },
                    churn: {
                        serviceUsers: suChurn,
                        employees: empChurn,
                        admins: adminChurn
                    },
                    demographics: {
                        ...demoAnalysis,
                        retentionRate: retention
                    }
                });

            } catch (error) {
                console.error("Error calculating stats:", error);
            } finally {
                setLoading(false);
            }
        }
        calculateBusinessStats();
    }, [serverId, adminFilter]);

    // --- NAVIGATION HANDLER ---
    const goToDetail = (title, collectionName, metricType) => {
        navigate(`/servers/${serverId}/analytics/details`, {
            state: { title, collectionName, metricType, adminFilter }
        });
    };

    if (loading) return <div style={{ padding: 20 }}>Analyzing Business Data...</div>;

    // --- COMPONENTS ---

    const OverviewCard = ({ title, active, total, color, icon, collectionName }) => (
        <div 
            onClick={() => goToDetail(`${title} Overview`, collectionName, 'status')}
            style={{ 
                backgroundColor: 'white', borderRadius: '10px', padding: '25px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flex: '1', minWidth: '300px', 
                display: 'flex', flexDirection: 'column', borderTop: `4px solid ${color}`,
                cursor: 'pointer', transition: 'transform 0.1s'
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '14px', textTransform: 'uppercase', fontWeight: 'bold', color: '#666' }}>{title}</span>
                <span style={{ fontSize: '20px' }}>{icon}</span>
            </div>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#2c3e50' }}>{total}</div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px', fontSize: '13px', color: '#666' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, marginRight: '8px' }}></span>
                {active} Active <span style={{ margin: '0 8px' }}>|</span> <span style={{ color: '#a0aec0' }}>{total - active} Inactive</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: '#edf2f7', borderRadius: '3px', marginTop: '15px', overflow: 'hidden' }}>
                <div style={{ width: `${total > 0 ? (active/total)*100 : 0}%`, height: '100%', background: color }}></div>
            </div>
        </div>
    );

    const InsightCard = ({ label, value, subtext, color, onClick }) => (
        <div 
            onClick={onClick}
            style={{ 
                backgroundColor: 'white', borderRadius: '10px', padding: '20px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flex: '1', minWidth: '180px', 
                textAlign: 'center', borderBottom: `4px solid ${color}`,
                cursor: 'pointer', transition: 'transform 0.1s'
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
            <div style={{ fontSize: '13px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>{label}</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2d3748' }}>{value}</div>
            <div style={{ fontSize: '12px', color: color, fontWeight: '500' }}>{subtext}</div>
        </div>
    );

    // Reused Component for both Growth and Churn (Handles all buckets)
    const ActivityTable = ({ title, data, color, collectionName, isChurn }) => (
        <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '25px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', flex: '1', minWidth: '300px' }}>
            <h3 
                onClick={() => goToDetail(`${title} ${isChurn ? 'Churn' : 'Growth'}`, collectionName, isChurn ? 'churn' : 'starters')}
                style={{ 
                    margin: '0 0 20px 0', color: '#2c3e50', fontSize: '16px', 
                    borderBottom: '1px solid #eee', paddingBottom: '10px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}
            >
                {title} <span style={{ color: '#a0aec0', fontWeight: 'normal' }}>{isChurn ? 'Inactive' : 'Acquired'}</span> <span style={{ fontSize: '12px', color: color }}>View →</span>
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <tbody>
                    {[
                        { label: 'Last Hour', val: data.hour },
                        { label: 'Last 24 Hours', val: data.day },
                        { label: 'Last 7 Days', val: data.week },
                        { label: 'Last 30 Days', val: data.month },
                        { label: 'Last 90 Days', val: data.quarter },
                        { label: 'Year to Date', val: data.year },
                    ].map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f7fafc' }}>
                            <td style={{ padding: '12px 0', color: '#666' }}>{row.label}</td>
                            <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold', color: row.val > 0 ? color : '#cbd5e0' }}>
                                {isChurn && row.val > 0 ? '-' : (row.val > 0 ? '+' : '')}{row.val > 0 ? row.val : '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div style={{ padding: '40px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
                <div>
                    <h1 style={{ margin: 0, fontSize: '24px', color: '#2c3e50' }}>{adminFilter ? `Analytics: ${adminNameDisplay}` : 'System Business Development'}</h1>
                    <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>Real-time tracking of workforce, clients, and retention.</p>
                </div>
            </div>

            {/* 1. OVERVIEW */}
            <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>📊 Resources Overview</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px', marginBottom: '40px' }}>
                <OverviewCard title="Service Users" active={stats.overview.suActive} total={stats.overview.suTotal} color="#38a169" icon="🏥" collectionName="serviceUsers" />
                <OverviewCard title="Employees" active={stats.overview.empActive} total={stats.overview.empTotal} color="#3182ce" icon="👔" collectionName="employees" />
                {!adminFilter && <OverviewCard title="Admins" active={stats.overview.adminTotal} total={stats.overview.adminTotal} color="#805ad5" icon="🛡️" collectionName="admins" />}
            </div>

            {/* 2. SALES */}
            <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>Sales Pipeline</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '40px' }}>
                <InsightCard label="Total Leads" value={stats.sales.totalLeads} subtext="Click to View" color="#ed64a6" onClick={() => goToDetail('All Leads', 'leads', 'leads')} />
                
                <InsightCard 
                    label="Converted" 
                    value={stats.sales.converted} 
                    subtext={`${stats.sales.conversionRate}% Rate (${stats.sales.converted} out of ${stats.sales.totalLeads})`} 
                    color="#48bb78" 
                    onClick={() => goToDetail('Converted Leads', 'leads', 'leads')} 
                />
                
                <InsightCard label="Open Opportunities" value={stats.sales.open} subtext="Active Leads" color="#ecc94b" onClick={() => goToDetail('Open Leads', 'leads', 'leads')} />
                <InsightCard label="Recent Inbound" value={`+${stats.sales.recentLeads}`} subtext="Last 30 Days" color="#4299e1" onClick={() => goToDetail('Recent Leads', 'leads', 'leads')} />
            </div>

            {/* 3. WORKFORCE */}
            <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>🧠 Workforce Intelligence</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '40px' }}>
                <InsightCard label="Retention Rate" value={`${stats.demographics.retentionRate}%`} subtext="Active vs Archived" color="#d53f8c" onClick={() => goToDetail('Retention Analysis', 'employees', 'retention')} />
                <InsightCard label="Avg Tenure" value={stats.demographics.avgTenure} subtext="Years of Service" color="#3182ce" onClick={() => goToDetail('Employee Tenure', 'employees', 'tenure')} />
                <InsightCard label="Avg Age" value={stats.demographics.avgAge} subtext="Years Old" color="#805ad5" onClick={() => goToDetail('Employee Age', 'employees', 'age')} />
                <InsightCard label="Recent Starters" value={`+${stats.demographics.starters30Days}`} subtext="Last 30 Days" color="#38a169" onClick={() => goToDetail('Recent Starters', 'employees', 'starters')} />
            </div>

            {/* 4. GROWTH VELOCITY */}
            <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>📈 Growth Velocity</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px', marginBottom: '40px' }}>
                <ActivityTable title="Service Users" data={stats.growth.serviceUsers} color="#38a169" collectionName="serviceUsers" isChurn={false} />
                <ActivityTable title="Employees" data={stats.growth.employees} color="#3182ce" collectionName="employees" isChurn={false} />
                {!adminFilter && <ActivityTable title="Admins" data={stats.growth.admins} color="#805ad5" collectionName="admins" isChurn={false} />}
            </div>

            {/* 5. CHURN VELOCITY - Styled Normal Black */}
            <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>📉 Churn Velocity</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px', marginBottom: '40px' }}>
                <ActivityTable title="Service Users" data={stats.churn.serviceUsers} color="#e53e3e" collectionName="serviceUsers" isChurn={true} />
                <ActivityTable title="Employees" data={stats.churn.employees} color="#e53e3e" collectionName="employees" isChurn={true} />
                {!adminFilter && <ActivityTable title="Admins" data={stats.churn.admins} color="#e53e3e" collectionName="admins" isChurn={true} />}
            </div>

        </div>
    );
}